import type { CartSubscriptionData, CartFutureBillings } from '@cvo/ui';
import { addBillingCycles, chargesByCurrency, effectiveCharges, effectiveNet, discountGross, includedDiscount, type Plan, type Addon, type Discountable, type DetailedPriceCharge, type PlanFrequency, type SubscriptionFees } from '@cvo/domain';
import { capitalize } from '@cvo/domain/utils/stringUtils';
import { SUBSCRIPTION_FEES_ID, type ChargeAggregate, type AddonWithQuantity, type AllowedCartItem } from '~/types/cart';

/** Subscription-specific cart item metadata */
type SubscriptionMeta = {
  recurrent: boolean;
};

const FIXED_CHARGE_POSITION_START = 100;
const VAT_CHARGE_POSITION_START = 200;

export const useSubscriptionSelectionStore = defineStore(
  'subscriptionSelection',
  // eslint-disable-next-line max-lines-per-function
  () => {
    const i18n = useI18n();
    const { formatPrice } = usePriceFormatter({ trailingZeroDisplay: 'auto' });

    const baseCart = useCart<SubscriptionMeta>();
    const { sortedItems } = useCartSorting(baseCart.items);
    const { total, calculateFixedSubtotalByGroup } = useCartCalculator(baseCart.items);
    const { displayItems } = useCartDisplayItems(sortedItems);

    const plan = ref<Plan | null>(null);
    const addons = ref<AddonWithQuantity[]>([]);
    const startDate = ref<Date>(new Date());

    const userStore = useUserStore();
    const currency = computed(() => plan.value?.price.currency ?? userStore.currency);
    const subscriptionFees = computed(() => plan.value?.subscriptionFees);
    const { feesMap, hasSubsFees, feePrices, frequency  } = useFees(subscriptionFees);

    function aggregateCharge(charges: Map<string, ChargeAggregate>, charge: DetailedPriceCharge) {
      const existing = charges.get(charge.code);
      if (existing) {
        existing.amount += parseFloat(charge.amount);
      } else {
        charges.set(charge.code, {
          code: charge.code,
          type: charge.type,
          amount: parseFloat(charge.amount),
          rate: charge.rate,
        });
      }
    }

    const feesChargesMap = computed(() => {
      const charges = new Map<string, ChargeAggregate>();
      const signUpFee = feesMap.value.get('SIGN_UP');

      if (signUpFee && !isBillingFee(signUpFee)) {
        chargesByCurrency(signUpFee.additionalCharges, currency.value).forEach((charge) => aggregateCharge(charges, charge));
      }

      return charges;
    });


    const recurrentChargesMap = computed(() => {
      const charges = new Map<string, ChargeAggregate>();
      if (plan.value) {
        effectiveCharges(plan.value, currency.value).forEach((charge) => aggregateCharge(charges, charge));
      }

      for (const addon of addons.value) {
        effectiveCharges(addon, currency.value).forEach((charge) => aggregateCharge(charges, charge));
      }

      return charges;
    });

    const allChargesMap = computed(() => {
      return [recurrentChargesMap.value, feesChargesMap.value]
        .reduce((allCharges, chargeMap) => {
          chargeMap.forEach((charge, code) => {
            const existing = allCharges.get(code);
            allCharges.set(code, existing
              ? { ...existing, amount: existing.amount + charge.amount }
              : { ...charge });
          });
          return allCharges;
        }, new Map<string, ChargeAggregate>());
    });

    // Rebuild cart items whenever plan or addons change
    watch([
      i18n.locale,
      currency,
      () => plan.value?.code,
      () => addons.value.map(addon => addon.code),
      feesMap,
      allChargesMap,
    ], () => {
      rebuildCart();
    });

    function rebuildCart() {
      baseCart.clearItems();

      if (!plan.value) {
        return;
      }

      // Add plan item
      baseCart.addItem({
        id: plan.value.code,
        type: 'fixed',
        group: 'subscription',
        name: i18n.t(`custom.plans.${plan.value.code}.title`),
        description: i18n.t(`custom.plans.recurrency.${plan.value.frequency}.long`),
        value: effectiveNet(plan.value, currency.value),
        currency: currency.value,
        quantity: null,
        position: 0,
        recurrent: true,
        ...promoItemFields(plan.value),
      });

      // Add addon items
      let addonPosition = 1;
      for (const addon of addons.value) {
        baseCart.addItem({
          id: addon.code,
          type: 'fixed',
          group: 'subscription',
          name: i18n.t(`custom.addons.${addon.code}.title`),
          description: i18n.t(`custom.plans.recurrency.${addon.frequency}.long`),
          value: effectiveNet(addon, currency.value),
          currency: currency.value,
          quantity: addon.quantity,
          position: addonPosition++,
          recurrent: true,
          ...promoItemFields(addon),
        });
      }

      // Add subscription fee item
      if (feesMap.value.has('SIGN_UP')) {
        addSubscriptionFeeItem(addonPosition + 1);
      }

      addChargeItems();
    }

    /** Strikethrough/tag fields for items whose value carries an auto-applied discount. */
    function promoItemFields(entity: Discountable) {
      return includedDiscount(entity)
        ? { originalValue: parseFloat(entity.price.net), promo: true }
        : {};
    }

    function addSubscriptionFeeItem(position: number) {
      baseCart.addItem({
        currency: currency.value,
        position,
        id: SUBSCRIPTION_FEES_ID,
        type: 'fixed',
        group: 'subscription',
        name: i18n.t('ui.registration.cart.subscription.programFee'),
        description: capitalize(i18n.t('ui.plans.fee.frequency.ONCE')),
        value: feePrices.value.signUp.net,
        quantity: null,
        recurrent: false,
      });
    }

    function addChargeItems() {
      const charges = Array.from(allChargesMap.value.values());
      const fixedCharges = charges.filter((charge) => charge.type === 'FIXED');
      const vatCharges = charges.filter((charge) => charge.type === 'VAT');

      addChargesByType(
        fixedCharges,
        FIXED_CHARGE_POSITION_START,
        'fees',
        (charge) => i18n.t(`ui.components.cart.charges.${charge.code}`),
      );

      addChargesByType(
        vatCharges,
        VAT_CHARGE_POSITION_START,
        'subscription',
        (charge) => i18n.t(`ui.components.cart.charges.${charge.type}`, { value: charge.rate?.display ?? '' }),
      );
    }

    function addChargesByType(
      charges: ChargeAggregate[],
      positionStart: number,
      groupName: string,
      nameFormatter: (charge: ChargeAggregate) => string,
    ) {

      charges.forEach((charge, index) => {
        baseCart.addItem({
          id: `charge-${charge.code}`,
          type: 'fixed',
          group: groupName,
          name: nameFormatter(charge),
          value: charge.amount,
          currency: currency.value,
          quantity: null,
          position: positionStart + index,
          recurrent: false,
        });
      });
    }

    function addAddon(addon: Addon) {
      if (!addons.value.some((existingAddon) => existingAddon.code === addon.code)) {
        addons.value.push({ ...addon, quantity: 1 });
      }
    }

    function removeAddon(addon: Addon) {
      addons.value = addons.value.filter((existingAddon) => existingAddon.code !== addon.code);
    }

    function resetSelection() {
      baseCart.clearItems();
      plan.value = null;
      addons.value = [];
      startDate.value = new Date();
      nextChargeDate.value = null;
    }

    function resetAddons() {
      addons.value = [];
    }

    const nextChargeDate = ref<Date | null>(null);
    const formattedNextChargeDate = useDateFormat(nextChargeDate, { dateFormat: 'short' });

    // Calculates the total recurrent subtotal for a given group.
    function calculateRecurrentSubtotal(
      group: AllowedCartItem['group'],
      subtotalMap: Map<string, ChargeAggregate>,
    ): number {
      const recurrentCharges = Array.from(subtotalMap.values()).reduce((sum, charge) => sum + charge.amount, 0);
      const subTotal = calculateFixedSubtotalByGroup(group, (item) => !!item.recurrent);

      return subTotal + recurrentCharges;
    }

    const recurrentTotal = computed(() => calculateRecurrentSubtotal(
      'subscription',
      recurrentChargesMap.value,
    ));

    /** Aggregated gross savings per billing cycle across the selected plan and add-ons. */
    const promoSavings = computed(() => {
      const planSavings = plan.value ? discountGross(plan.value, currency.value) : 0;

      return addons.value.reduce(
        (savings, addon) => savings + discountGross(addon, currency.value) * addon.quantity,
        planSavings,
      );
    });

    /**
     * Date of the first non-promotional charge, anchored on the billing-cycle-aware
     * `nextChargeDate` (known once the payment page fetches the billing calendar) — the first
     * renewal opens period 2, so the price reverts `periods - 1` cycles later. Null while the
     * next charge date is unknown: the FE cannot derive it from today without the cycle anchor.
     */
    const firstFullPaymentDate = computed<Date | null>(() => {
      const discount = plan.value ? includedDiscount(plan.value) : undefined;
      if (!plan.value || discount?.duration.type !== 'FIXED' || !discount.duration.periods || !nextChargeDate.value) {
        return null;
      }

      return addBillingCycles(nextChargeDate.value, plan.value.frequency, discount.duration.periods - 1);
    });
    const formattedFirstFullPaymentDate = useDateFormat(firstFullPaymentDate, { dateFormat: 'short' });

    /** Formats a recurring amount as "{price} /{frequency}", e.g. "€109.00 /mo.". */
    function formatRecurringPrice(amount: number, planFrequency: PlanFrequency): string {
      const price = formatPrice({ amount: amount.toString(), currency: currency.value });

      return `${price} /${i18n.t(`custom.plans.recurrency.${planFrequency}.short`)}`;
    }

    /**
     * Recurring totals carrying their billing cadence ("€109.00 /mo."): the promotional price and
     * the one before any discount. Undefined until a plan is selected, since the cadence comes
     * from its frequency. Use these wherever a price is quoted inside a sentence — the bare
     * `cartSummary` / `promoSummary` amounts only read unambiguously next to a frequency label.
     */
    const recurringPrices = computed(() => {
      const planFrequency = plan.value?.frequency;
      if (!planFrequency) {
        return undefined;
      }

      return {
        promo: formatRecurringPrice(recurrentTotal.value, planFrequency),
        full: formatRecurringPrice(recurrentTotal.value + promoSavings.value, planFrequency),
      };
    });

    /** "Future billings" row data: promo recurring price + struck full price, until the first full payment. */
    const futureBillings = computed((): CartFutureBillings | undefined => {
      const prices = recurringPrices.value;
      if (!prices || !promoSummary.value) {
        return undefined;
      }

      return {
        label: i18n.t('ui.registration.cart.futureBillings.title'),
        description: firstFullPaymentDate.value
          ? i18n.t('ui.registration.cart.futureBillings.firstFullPayment', {
            date: formattedFirstFullPaymentDate.value,
          })
          : undefined,
        price: prices.promo,
        originalValue: prices.full,
      };
    });

    /** Recurring total before any promotional discount; equals `recurringTotal` when none applies. */
    const fullRecurringTotal = computed(() => formatPrice({
      amount: (recurrentTotal.value + promoSavings.value).toString(),
      currency: currency.value,
    }));

    /**
     * Promo facts for the legal disclaimer; null unless the plan carries a FIXED-duration
     * auto-applied discount (a LIFETIME discount never reverts, so the regular copy holds).
     */
    const promoSummary = computed(() => {
      const discount = plan.value ? includedDiscount(plan.value) : undefined;
      if (discount?.duration.type !== 'FIXED' || !discount.duration.periods) {
        return null;
      }

      return {
        fullRecurringTotal: fullRecurringTotal.value,
        periods: discount.duration.periods,
      };
    });

    const cartSummary = computed((): CartSubscriptionData => {
      const hasSavings = promoSavings.value > 0;

      return {
        items: displayItems.value,
        total: formatPrice({ amount: total.value.toString(), currency: currency.value }),
        nextChargeDate: formattedNextChargeDate?.value ?? '',
        recurringTotal: formatPrice({ amount: recurrentTotal.value.toString(), currency: currency.value }),
        savingsText: hasSavings
          ? i18n.t('ui.registration.cart.savings.tag', {
            amount: formatPrice({ amount: promoSavings.value.toString(), currency: currency.value }),
          })
          : undefined,
        totalDescription: hasSavings ? i18n.t('ui.registration.cart.savings.description') : undefined,
        futureBillings: futureBillings.value,
      };
    });

    const hasEqualTotals = computed(() => cartSummary.value.total === cartSummary.value.recurringTotal);

    function isAddonSelected(addonCode: string): boolean {
      return addons.value.some((addon) => addon.code === addonCode);
    }

    return {
      plan,
      addons,

      // Cart
      items: baseCart.items,
      isValid: baseCart.hasItems,
      removeItem: baseCart.removeItem,
      updateItemQuantity: baseCart.updateItemQuantity,
      // Addons to cart
      addAddon,
      removeAddon,
      isAddonSelected,

      startDate,
      nextChargeDate,
      total,
      cartSummary,
      promoSummary,
      fullRecurringTotal,
      recurringPrices,
      hasEqualTotals,
      resetSelection,
      resetAddons,

      // Subscription fees
      feesMap,
      hasSubsFees,
      feePrices,
      frequency,
    };
  },
  {
    persist: {
      omit: ['nextChargeDate'],
      afterHydrate: ({ store }) => {
        // A plan persisted before fees carried a gross price list can't be priced by useFees.
        if (store.plan?.subscriptionFees?.some((fee: SubscriptionFees) => !Array.isArray(fee.gross))) {
          store.resetSelection();
        }
      },
    },
  },
);
